import {
    Component,
    input,
    output,
    computed,
    ElementRef,
    signal,
    ChangeDetectionStrategy,
    HostListener,
    viewChild,
    model,
    inject,
    DestroyRef,
} from "@angular/core";

import { TypeManagerDecorator } from "../../../main/type.map.service";
import { Workflow } from "../service/Workflow";
import { Task } from "../../../pojo/Task";
import { S25IconButtonComponent } from "../../../standalone/s25.icon.button.component";
import { ApprovalGroupsStore } from "../../approval-groups/approval.groups.store";
import { s25Resource } from "../../../util/s25.resource";
import { Item } from "../../../pojo/Item";
import { S25Util } from "../../../util/s25-util";
import { NameService } from "../../../services/name.service";
import { DataIngestionService } from "../../data-ingestion/data.ingestion.service";
import { DiagramSelectEvent, WorkflowDiagramStagesComponent } from "./workflow.diagram.stages.component";
import { WorkflowService } from "../service/workflow.service";
import { S25LoadingInlineStaticComponent } from "../../s25-loading/s25.loading.inline.static.component";
import { AriaLive } from "../../../services/aria.live.service";
import { TelemetryService } from "../../../services/telemetry.service";
import { SeSecurityStore } from "../../student-engagement/se.security.store";
import { FlsStore } from "../../../stores/fls.store";
import { S25Assert } from "../../../util/s25.assert";
import {
    actionLabel,
    configuredApprovalRows,
    DiagramActionRow,
    DiagramChatRequest,
    DiagramSelection,
    DiagramStage,
    DiagramState,
    filterUsedActions,
    LegendOrder,
    liveApprovalRows,
    nameLookups,
    ObjectNames,
    paneHeading,
    resolveSelection,
    RESTRICTED_CONTACT_LABEL,
    RESTRICTED_VALUE_LABEL,
    spawnedGroup,
    stageBaseType,
    stageBodyHeight,
    stageColorType,
    stageFlagLabels,
    DiagramRowHeights,
    StageStatus,
    StageStatusInfo,
    spawnFormLabel,
    spawnTargetFormIds,
    StageTypeStyles,
    toPersonRow,
} from "./workflow.diagram.model";
import { DiagramPaneMode, WorkflowDiagramDetailPaneComponent } from "./workflow.diagram.detail.pane.component";

const TELEMETRY_CATEGORY = "WorkflowDiagram";
/** Below this viewer width the detail pane becomes a modal sheet over the canvas. */
const SHEET_BREAKPOINT_PX = 720;
const REFRESH_PENDING_ANNOUNCE_DELAY_MS = 400;
const ESCAPE_OWNER_SELECTOR = "workflow-chat-panel, ngb-modal-window, [aria-modal='true']";

interface DiagramTransition {
    id: string;
    from: string;
    to: string;
    label: string;
    type: Workflow.WorkflowStageTransition["transitionType"];
    path?: string;
}

@TypeManagerDecorator("workflow-diagram-viewer")
@Component({
    selector: "workflow-diagram-viewer",
    template: `
        <div class="diagram-header" data-drag-handle>
            <div class="header-left">
                <h3>{{ workflowProcess()?.workflowProcessName || "Workflow Diagram" }}</h3>
                <span class="stage-count">{{ diagramStages().length }} stages</span>
            </div>
            <div class="diagram-controls">
                <div class="zoom-controls">
                    <s25-ng-icon-button
                        [type]="'zoomOut'"
                        [ariaLabel]="'Zoom out'"
                        (clicked)="zoomOut($event)"
                        [disabled]="zoom() <= MIN_ZOOM"
                    ></s25-ng-icon-button>
                    <span class="zoom-level">{{ (zoom() * 100).toFixed(0) }}%</span>
                    <s25-ng-icon-button
                        [type]="'zoomIn'"
                        [ariaLabel]="'Zoom in'"
                        (clicked)="zoomIn($event)"
                        [disabled]="zoom() >= MAX_ZOOM"
                    ></s25-ng-icon-button>
                    <button
                        class="aw-button aw-button--outline fit-to-view-btn"
                        (click)="fitToView($event)"
                        title="Fit entire diagram in view"
                    >
                        Fit to View
                    </button>
                    <button
                        class="aw-button aw-button--outline fit-to-view-btn"
                        (click)="resetZoom($event)"
                        title="Reset to default zoom (100%)"
                    >
                        Reset Zoom
                    </button>
                    <s25-ng-icon-button
                        [type]="compactMode() ? 'expand' : 'collapse'"
                        [size]="25"
                        [ariaLabel]="compactMode() ? 'Expand diagram' : 'Collapse diagram'"
                        (clicked)="compactMode.set(!compactMode())"
                    />
                    @if (showProgress() && processInstanceId()) {
                        <s25-ng-icon-button
                            [type]="'refresh'"
                            [ariaLabel]="'Refresh workflow status'"
                            (clicked)="refreshStatus()"
                        />
                    }
                    <s25-ng-icon-button [type]="'close'" [ariaLabel]="'Close'" (clicked)="showDiagram.set(false)" />
                </div>
            </div>
        </div>

        @if (diagramFailed()) {
            <div class="diagram-error" role="status">
                <span>Couldn't load workflow status</span>
                <button type="button" class="aw-button aw-button--outline" (click)="retryDiagram()">Retry</button>
            </div>
        }

        <div class="diagram-body">
            <div class="diagram-canvas-wrapper" #canvasWrapper (wheel)="onWheel($event)">
                @if (showZoomIndicator()) {
                    <div class="zoom-indicator">{{ (zoom() * 100).toFixed(0) }}%</div>
                }
                <div
                    class="diagram-zoom-container"
                    [style.transform]="'scale(' + zoom() + ')'"
                    [style.transform-origin]="'0 0'"
                >
                    @if (isFirstLoad()) {
                        <s25-ng-loading-inline-static />
                    } @else {
                        <div class="diagram-stack">
                            <svg
                                class="workflow-diagram"
                                [attr.width]="svgWidth()"
                                [attr.height]="svgHeight()"
                                [attr.viewBox]="viewBox()"
                            >
                                <defs>
                                    <!-- Gradient backgrounds for stages (scoped by svgId) -->
                                    @for (colorType of legendOrder; track colorType) {
                                        <linearGradient
                                            [id]="'grad-' + colorType + '-' + svgId"
                                            x1="0%"
                                            y1="0%"
                                            x2="0%"
                                            y2="100%"
                                        >
                                            <stop
                                                offset="0%"
                                                [attr.stop-color]="stageTypeStyles[colorType].gradient[0]"
                                            />
                                            <stop
                                                offset="100%"
                                                [attr.stop-color]="stageTypeStyles[colorType].gradient[1]"
                                            />
                                        </linearGradient>
                                    }

                                    <!-- Arrow markers (scoped by svgId) -->
                                    <marker
                                        [id]="'arrow-approve-' + svgId"
                                        markerWidth="12"
                                        markerHeight="12"
                                        refX="10"
                                        refY="6"
                                        orient="auto"
                                    >
                                        <path d="M2,2 L10,6 L2,10 L4,6 Z" fill="#666" />
                                    </marker>
                                    <marker
                                        [id]="'arrow-deny-' + svgId"
                                        markerWidth="12"
                                        markerHeight="12"
                                        refX="10"
                                        refY="6"
                                        orient="auto"
                                    >
                                        <path d="M2,2 L10,6 L2,10 L4,6 Z" fill="#666" />
                                    </marker>
                                    <marker
                                        [id]="'arrow-changes-' + svgId"
                                        markerWidth="12"
                                        markerHeight="12"
                                        refX="10"
                                        refY="6"
                                        orient="auto"
                                    >
                                        <path d="M2,2 L10,6 L2,10 L4,6 Z" fill="#666" />
                                    </marker>
                                    <marker
                                        [id]="'arrow-cancel-' + svgId"
                                        markerWidth="12"
                                        markerHeight="12"
                                        refX="10"
                                        refY="6"
                                        orient="auto"
                                    >
                                        <path d="M2,2 L10,6 L2,10 L4,6 Z" fill="#666" />
                                    </marker>
                                    <marker
                                        [id]="'arrow-default-' + svgId"
                                        markerWidth="12"
                                        markerHeight="12"
                                        refX="10"
                                        refY="6"
                                        orient="auto"
                                    >
                                        <path d="M2,2 L10,6 L2,10 L4,6 Z" fill="#666" />
                                    </marker>

                                    <!-- Shadow filter (scoped by svgId) -->
                                    <filter [id]="'shadow-' + svgId" x="-50%" y="-50%" width="200%" height="200%">
                                        <feGaussianBlur in="SourceAlpha" stdDeviation="3" />
                                        <feOffset dx="0" dy="2" result="offsetblur" />
                                        <feComponentTransfer>
                                            <feFuncA type="linear" slope="0.2" />
                                        </feComponentTransfer>
                                        <feMerge>
                                            <feMergeNode />
                                            <feMergeNode in="SourceGraphic" />
                                        </feMerge>
                                    </filter>
                                </defs>

                                <!-- Group for all transitions and non-hovered labels (drawn first, behind stages) -->
                                <g class="transitions-layer">
                                    @for (transition of diagramTransitions(); track transition.id) {
                                        <g class="transition-group">
                                            <path
                                                [attr.d]="transition.path"
                                                [attr.stroke]="'#666'"
                                                stroke-width="2"
                                                fill="none"
                                                [attr.marker-end]="
                                                    'url(#arrow-' +
                                                    getArrowMarkerId(transition.type) +
                                                    '-' +
                                                    svgId +
                                                    ')'
                                                "
                                                class="transition-line"
                                                pointer-events="stroke"
                                            />
                                        </g>
                                    }
                                </g>

                                <!-- Stages (drawn on top of transitions) -->
                                <g
                                    class="stages-layer"
                                    workflow-diagram-stages
                                    [stages]="diagramStages()"
                                    [compactMode]="compactMode()"
                                    [showProgress]="isLive()"
                                    [chatEnabled]="chatEnabled()"
                                    [paneId]="paneContent() ? paneId : undefined"
                                    [svgId]="svgId"
                                    (selectItem)="onSelect($event)"
                                    (chatWith)="requestChat($event)"
                                    (spawnedMore)="onSpawnedMore()"
                                ></g>

                                <!-- Progress indicator  -->
                                @if (progressNodes().length) {
                                    <g class="progress-indicator-layer">
                                        @for (seg of progressSegments(); track seg.x1) {
                                            <line
                                                [attr.x1]="seg.x1"
                                                [attr.y1]="PROGRESS_Y"
                                                [attr.x2]="seg.x2"
                                                [attr.y2]="PROGRESS_Y"
                                                [attr.stroke]="seg.filled ? progressNodeColor(seg.status) : '#d1d5db'"
                                                stroke-width="3"
                                                stroke-linecap="round"
                                            />
                                        }
                                        @for (node of progressNodes(); track node.x) {
                                            @if (node.status === "current") {
                                                <circle
                                                    [attr.cx]="node.x"
                                                    [attr.cy]="PROGRESS_Y"
                                                    [attr.r]="PROGRESS_R"
                                                    fill="white"
                                                    stroke="#3b82f6"
                                                    stroke-width="3"
                                                />
                                                <circle
                                                    [attr.cx]="node.x"
                                                    [attr.cy]="PROGRESS_Y"
                                                    r="4"
                                                    fill="#3b82f6"
                                                    class="current-pulse"
                                                />
                                            } @else if (node.status === "pending") {
                                                <circle
                                                    [attr.cx]="node.x"
                                                    [attr.cy]="PROGRESS_Y"
                                                    [attr.r]="PROGRESS_R"
                                                    fill="#f1f5f9"
                                                    stroke="#d1d5db"
                                                    stroke-width="2"
                                                />
                                            } @else {
                                                <circle
                                                    [attr.cx]="node.x"
                                                    [attr.cy]="PROGRESS_Y"
                                                    [attr.r]="PROGRESS_R"
                                                    [attr.fill]="progressNodeColor(node.status)"
                                                />
                                                <text
                                                    [attr.x]="node.x"
                                                    [attr.y]="PROGRESS_Y + 1"
                                                    text-anchor="middle"
                                                    dominant-baseline="middle"
                                                    fill="white"
                                                    font-size="14"
                                                    font-weight="bold"
                                                >
                                                    {{ progressNodeIcon(node.status) }}
                                                </text>
                                            }
                                            @if (node.label) {
                                                <rect
                                                    [attr.x]="node.x - node.labelWidth / 2"
                                                    [attr.y]="PILL_CENTER_Y - 9"
                                                    [attr.width]="node.labelWidth"
                                                    [attr.height]="18"
                                                    [attr.rx]="9"
                                                    [attr.fill]="progressPillColor(node.status)"
                                                />
                                                <text
                                                    [attr.x]="node.x"
                                                    [attr.y]="PILL_CENTER_Y"
                                                    text-anchor="middle"
                                                    dominant-baseline="middle"
                                                    [attr.fill]="progressPillTextColor(node.status)"
                                                    font-size="10"
                                                    font-weight="bold"
                                                    style="text-transform: uppercase; letter-spacing: 0.5px"
                                                >
                                                    {{ node.label }}
                                                </text>
                                            }
                                        }
                                    </g>
                                }
                            </svg>
                            @if (!compactMode()) {
                                <div class="transition-labels-layer">
                                    @for (transition of labeledTransitions(); track transition.id) {
                                        @let labelPos = getTransitionLabelPosition(transition);
                                        <span
                                            class="transition-label"
                                            [style.left.px]="labelPos.x"
                                            [style.top.px]="labelPos.y"
                                            >{{ transition.label }}</span
                                        >
                                    }
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>

            @if (paneContent(); as content) {
                <workflow-diagram-detail-pane
                    [content]="content"
                    [mode]="paneMode()"
                    [live]="isLive()"
                    [chatEnabled]="chatEnabled()"
                    [names]="objectNames.value()"
                    [fullHttpDetail]="fullHttpDetail()"
                    [paneId]="paneId"
                    (closed)="closePane()"
                    (chatWith)="requestChatFromPane($event)"
                    (selectItem)="selectFromPane($event)"
                />
            }
        </div>

        <div class="diagram-legend">
            <div class="legend-title" id="{{ svgId }}-legend">Stage Types</div>
            <ul class="legend-items" [attr.aria-labelledby]="svgId + '-legend'">
                @for (colorType of legendOrder; track colorType) {
                    @let style = stageTypeStyles[colorType];
                    <li class="legend-item">
                        <span
                            class="legend-icon"
                            aria-hidden="true"
                            [style.background]="
                                'linear-gradient(135deg, ' + style.gradient[0] + ', ' + style.gradient[1] + ')'
                            "
                            >{{ style.glyph }}</span
                        >
                        <span>{{ style.label }}</span>
                    </li>
                }
            </ul>
        </div>
    `,
    host: {
        "[class.compact-mode]": "compactMode()",
    },
    styles: `
        :host {
            position: relative;
            display: flex;
            flex-direction: column;
            flex: 1 1 auto;
            min-height: 0;
            height: 100%;
            background: var(--color--surface-2-bg);
            border-radius: 12px;
            overflow: hidden;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
        }

        .diagram-header,
        .diagram-error,
        .diagram-legend {
            flex-shrink: 0;
        }

        .diagram-header {
            padding: 16px 20px;
            background: var(--color--surface-bg);
            border-bottom: 2px solid var(--color--border);
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .header-left {
            display: flex;
            align-items: baseline;
            gap: 12px;
        }

        .diagram-header h3 {
            margin: 0;
            font-size: 18px;
            font-weight: 700;
            color: var(--color--text-strong);
        }

        .stage-count {
            font-size: 13px;
            color: var(--color--text-muted);
            font-weight: 500;
        }

        .diagram-controls {
            display: flex;
            gap: 16px;
            align-items: center;
        }

        .zoom-controls {
            display: flex;
            gap: 8px;
            align-items: center;
        }

        .zoom-level {
            min-width: 50px;
            text-align: center;
            font-size: 13px;
            font-weight: 600;
            color: var(--color--text);
            padding: 0 8px;
        }

        .fit-to-view-btn {
            font-size: 12px;
            padding: 4px 12px;
            height: 28px;
            min-width: auto;
        }

        .diagram-body {
            position: relative;
            display: flex;
            flex: 1;
            min-height: 0;
        }

        .diagram-body workflow-diagram-detail-pane:not(.is-dialog) {
            width: 340px;
            flex-shrink: 0;
        }

        .diagram-canvas-wrapper {
            flex: 1;
            min-width: 0;
            position: relative;
            overflow: auto;
            background: var(--color--surface-2-bg);
        }

        .diagram-error {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 8px 20px;
            background: var(--color--surface-bg);
            border-bottom: 1px solid var(--color--border);
            color: var(--color--text);
            font-size: 13px;
        }

        .diagram-zoom-container {
            display: inline-block;
            transition: transform 0.2s ease;
            transform-origin: 0 0;
            padding: 40px;
            min-width: min-content;
        }

        .workflow-diagram {
            display: block;
        }

        /* Transition styles */
        .transition-line {
            transition: all 0.2s ease;
        }

        .transition-line:hover {
            stroke-width: 4;
            opacity: 1 !important;
        }

        .diagram-stack {
            position: relative;
        }

        .transition-labels-layer {
            position: absolute;
            inset: 0;
            pointer-events: none;
        }

        .transition-label {
            position: absolute;
            transform: translate(-50%, -50%);
            max-width: 120px;
            padding: 2px 8px;
            overflow: hidden;
            white-space: nowrap;
            text-overflow: ellipsis;
            font-size: 0.75rem;
            font-weight: 500;
            color: var(--color--text-muted);
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 3px;
            pointer-events: auto;
            cursor: default;
        }

        .transition-label:hover {
            max-width: none;
            z-index: 1;
            color: var(--color--text-strong);
            box-shadow:
                0 7px 14px 0 rgba(50, 50, 93, 0.1),
                0 3px 6px 0 rgba(0, 0, 0, 0.08);
        }

        .diagram-legend {
            padding: 16px 20px;
            background: var(--color--surface-bg);
            border-top: 2px solid var(--color--border);
            display: flex;
            gap: 24px;
            align-items: center;
        }

        .legend-title {
            font-size: 13px;
            font-weight: 700;
            color: var(--color--text);
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }

        .legend-items {
            display: flex;
            flex-wrap: wrap;
            gap: 20px;
            margin: 0;
            padding: 0;
            list-style: none;
        }

        .legend-item {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 13px;
            color: var(--color--text);
            font-weight: 500;
        }

        .legend-icon {
            width: 24px;
            height: 24px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 12px;
            font-weight: bold;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        /* Zoom indicator */
        .zoom-indicator {
            position: absolute;
            top: 20px;
            right: 20px;
            background: rgba(0, 0, 0, 0.8);
            color: white;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 16px;
            font-weight: 600;
            z-index: 100;
            display: flex;
            align-items: center;
            gap: 8px;
            animation: fadeIn 0.2s ease;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: translateY(-10px);
            }
            to {
                opacity: 1;
                transform: translateY(0);
            }
        }

        .current-pulse {
            animation: progressPulse 1.5s ease-in-out infinite;
        }

        @keyframes progressPulse {
            0%,
            100% {
                opacity: 1;
                r: 4;
            }
            50% {
                opacity: 0.6;
                r: 6;
            }
        }

        /* Compact mode */
        .compact-mode .diagram-canvas-wrapper {
            min-height: 200px;
        }

        ::ng-deep .zoom-controls svg.icon {
            color: var(--color--text);
        }

        @media (max-width: 480px) {
            :host {
                position: fixed;
                inset: 0;
                z-index: 1000;
                border-radius: 0;
                height: 100%;
                padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
                    env(safe-area-inset-left);
                background: var(--color--surface-bg);
            }

            .diagram-header {
                flex-direction: column;
                align-items: stretch;
                gap: 12px;
                padding: 12px 16px;
            }

            .header-left {
                justify-content: space-between;
                align-items: center;
            }

            .diagram-header h3 {
                font-size: 16px;
            }

            .diagram-controls {
                justify-content: flex-end;
            }

            .zoom-controls {
                flex-wrap: wrap;
                gap: 6px;
            }

            .zoom-level {
                min-width: 40px;
                font-size: 12px;
                padding: 0 4px;
            }

            .fit-to-view-btn {
                display: none;
            }

            .diagram-canvas-wrapper {
                -webkit-overflow-scrolling: touch;
            }

            .diagram-legend {
                flex-direction: column;
                align-items: flex-start;
                gap: 8px;
                padding: 12px 16px;
            }

            .legend-items {
                flex-wrap: wrap;
                gap: 12px;
            }

            .legend-item {
                font-size: 12px;
            }

            .legend-icon {
                width: 20px;
                height: 20px;
                font-size: 10px;
            }
        }
    `,
    imports: [
        S25IconButtonComponent,
        WorkflowDiagramStagesComponent,
        WorkflowDiagramDetailPaneComponent,
        S25LoadingInlineStaticComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WorkflowDiagramViewerComponent {
    workflowProcess = input<Workflow.ProcessInstance | null>(null);
    showProgress = input(false);
    /** Set by hosts that handle {@link chatWith}; definition-only hosts leave it off and get no chat buttons. */
    chatAvailable = input(false);
    showDiagram = model<boolean>();

    chatWith = output<DiagramChatRequest>();

    compactMode = signal(false);

    // Unique instance ID to scope SVG defs (gradients, markers, filters)
    protected readonly svgId = `wfd-${Math.random().toString(36).slice(2, 8)}`;
    protected readonly paneId = `${this.svgId}-pane`;
    protected readonly legendOrder = LegendOrder;
    protected readonly stageTypeStyles = StageTypeStyles;

    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly securityStore = inject(SeSecurityStore);
    private readonly flsStore = inject(FlsStore);
    private readonly destroyRef = inject(DestroyRef);

    // Zoom constraints
    readonly MIN_ZOOM = 0.25;
    readonly MAX_ZOOM = 3;
    readonly ZOOM_STEP = 0.25;
    readonly DEFAULT_ZOOM = 1.0;

    zoom = signal(this.DEFAULT_ZOOM);
    showZoomIndicator = signal(false);
    private zoomIndicatorTimeout?: number;
    canvasWrapper = viewChild<ElementRef<HTMLDivElement>>("canvasWrapper");

    protected readonly processInstanceId = computed(() => this.workflowProcess()?.workflowProcessInstanceId);

    readonly diagram = s25Resource({
        if: () => !!this.showProgress() && !!this.processInstanceId(),
        params: () => this.processInstanceId()!,
        loader: ({ params }) => WorkflowService.getWorkflowDiagram(params),
    });

    /**
     * Live data for this submission; undefined on definition-only hosts and when the first diagram.json load failed.
     * A failed refresh keeps the previous result for the same instance.
     */
    private readonly liveDiagram = computed(() => {
        if (!this.showProgress()) return undefined;
        const lastDiagram = this.diagram.lastValue();
        if (lastDiagram?.workflowProcessInstanceId !== this.processInstanceId()) return undefined;
        return lastDiagram;
    });
    protected readonly isLive = computed(() => !!this.liveDiagram());
    protected readonly isFirstLoad = computed(() => this.diagram.isLoading() && !this.diagram.value());
    protected readonly diagramFailed = computed(
        () => this.showProgress() && !this.diagram.isLoading() && !!this.diagram.error(),
    );

    protected readonly chatEnabled = computed(
        () => this.isLive() && this.chatAvailable() && this.securityStore.canAccessChat(),
    );
    /** Configured-mapping pane: full URL and payload template only for full-FLS viewers. */
    protected readonly fullHttpDetail = computed(() => this.flsStore.hasFull("STUDENT_ENGAGEMENT"));

    private readonly stageIdToDetail = computed(
        () =>
            new Map((this.liveDiagram()?.stageDetails ?? []).map((detail) => [detail.originalWorkflowStageId, detail])),
    );

    private readonly fieldTypeToIds = computed(() => nameLookups(this.liveDiagram()?.stageDetails));

    private readonly spawnFormIds = computed(() => [
        ...new Set(
            (this.workflowProcess()?.stages ?? []).flatMap((stage) =>
                (stage.actions ?? []).flatMap(spawnTargetFormIds),
            ),
        ),
    ]);

    readonly spawnFormNames = s25Resource({
        if: () => this.spawnFormIds().length > 0,
        params: () => this.spawnFormIds(),
        loader: ({ params }) => DataIngestionService.getIngestionPointNames(params),
    });

    readonly objectNames = s25Resource({
        if: () => this.fieldTypeToIds().size > 0,
        params: () => this.fieldTypeToIds(),
        loader: async ({ params }): Promise<ObjectNames> => {
            const nameMaps = await Promise.all(
                [...params].map(([fieldType, ids]) => DataIngestionService.getObjectNames(fieldType, ids)),
            );
            return Object.assign({}, ...nameMaps);
        },
    });

    private currentStageId = computed(() => this.liveDiagram()?.currentStageId ?? null);

    private stageStatuses = computed((): Map<number, StageStatusInfo> | undefined => {
        const status = this.liveDiagram();
        if (!status?.stages?.length) return undefined;

        const map = new Map<number, StageStatusInfo>();
        for (const stage of status.stages) {
            const stageId = stage.originalWorkflowStageId;

            const approvals = stage.approvals ?? [];
            const isCurrent = stageId === status.currentStageId;
            const isTerminal = stage.isTerminal ?? false;

            if (isCurrent) {
                const resolved = approvals.length
                    ? this.resolveStageCompletion(approvals)
                    : isTerminal
                      ? "completed"
                      : "current";
                // For terminal stages with any completed approval, treat as completed
                // (the workflow has effectively finished even if some approvals are pending)
                const hasAnyCompleted = isTerminal && approvals.some((a) => a.approvalStatus === Task.States.Completed);
                const effectiveStatus = hasAnyCompleted ? "completed" : resolved === "pending" ? "current" : resolved;
                const isCompletedStage = effectiveStatus === "completed";
                map.set(stageId, {
                    status: effectiveStatus,
                    label: this.summarizeApprovals(approvals, isCompletedStage),
                });
            } else if (approvals.length) {
                // For non-current stages with approvals, the workflow has moved past them
                // Treat as completed if any approval was resolved (even if some are still pending)
                const hasAnyResolved = this.hasResolvedApproval(approvals);
                const stageStatus = hasAnyResolved ? "completed" : this.resolveStageCompletion(approvals);
                map.set(stageId, {
                    status: stageStatus,
                    label: this.summarizeApprovals(approvals, stageStatus === "completed"),
                });
            }
        }

        return map.size ? map : undefined;
    });

    private resolveStageCompletion(approvals: { approvalStatus: Task.State }[]): StageStatus {
        const allTerminal = approvals.every(
            (a) =>
                a.approvalStatus === Task.States.Completed ||
                a.approvalStatus === Task.States.Denied ||
                a.approvalStatus === Task.States.Cancelled,
        );
        if (!allTerminal) return "pending";
        if (approvals.some((a) => a.approvalStatus === Task.States.Denied)) return "denied";
        if (approvals.some((a) => a.approvalStatus === Task.States.Cancelled)) return "cancelled";
        return "completed";
    }

    private hasResolvedApproval(approvals: { approvalStatus: Task.State }[]): boolean {
        if (!approvals?.length) return false;
        return approvals.some(
            (a) =>
                a.approvalStatus === Task.States.Completed ||
                a.approvalStatus === Task.States.Denied ||
                a.approvalStatus === Task.States.Cancelled ||
                a.approvalStatus === Task.States.RequestedChanges,
        );
    }

    private summarizeApprovals(approvals: { approvalStatus: Task.State }[], isCompletedStage: boolean = false): string {
        if (!approvals.length) return isCompletedStage ? "Completed" : "Awaiting Approval";
        if (approvals.some((a) => a.approvalStatus === Task.States.Denied)) return "Denied";
        if (approvals.some((a) => a.approvalStatus === Task.States.RequestedChanges)) return "Changes Requested";
        if (approvals.some((a) => a.approvalStatus === Task.States.Cancelled)) return "Canceled";
        const completedCount = approvals.filter((a) => a.approvalStatus === Task.States.Completed).length;
        if (completedCount === approvals.length) return "Approved";
        // For completed stages (workflow moved past), just show "Approved" instead of partial count
        if (isCompletedStage && completedCount > 0) return "Approved";
        if (completedCount > 0) return `${completedCount}/${approvals.length} Approved`;
        return "Awaiting Approval";
    }

    workflowContacts = computed(() =>
        this.workflowProcess()
            ?.stages?.flatMap((stage) => stage.states)
            ?.flatMap((state) => state!.contacts)
            ?.map((contact) => ({ itemId: contact!.contId, itemTypeId: Item.Ids.Contact })),
    );

    approvalContactNames = s25Resource({
        if: () => !!this.workflowContacts()?.length,
        loader: async () => {
            const result: Record<number, string> = {};
            const [contactMap] = await S25Util.Maybe(NameService.getNames(this.workflowContacts()!));
            if (contactMap) {
                for (const [contId, contactName] of Object.entries(contactMap)) {
                    const nameKey = Number(contId.split("-")[0]);
                    if (!isNaN(nameKey)) result[nameKey] = contactName;
                }
                return result;
            }
        },
    });

    apGroupStore = inject(ApprovalGroupsStore);

    readonly selection = signal<DiagramSelection | null>(null);
    /** Element that opened the pane; focus returns here when it closes. */
    private selectionTrigger: HTMLElement | null = null;

    protected readonly paneContent = computed(() => {
        const selection = this.selection();
        return selection ? resolveSelection(this.diagramStages(), selection) : null;
    });

    /** Fed by a ResizeObserver on the host; the viewer's own width, not the viewport's, picks pane vs sheet. */
    private readonly viewerWidth = signal(Number.POSITIVE_INFINITY);
    protected readonly paneMode = computed<DiagramPaneMode>(() =>
        this.viewerWidth() < SHEET_BREAKPOINT_PX ? "dialog" : "region",
    );

    constructor() {
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(([entry]) => this.viewerWidth.set(entry.contentRect.width));
        observer.observe(this.elementRef.nativeElement);
        this.destroyRef.onDestroy(() => observer.disconnect());
    }

    @HostListener("document:keydown.escape", ["$event"])
    onEscape(event: KeyboardEvent) {
        if (!this.showDiagram() || event.defaultPrevented) return;
        const target = event.target instanceof Element ? event.target : null;
        if (target?.closest(ESCAPE_OWNER_SELECTOR)) return;
        if (this.selection()) {
            this.closePane();
            return;
        }
        this.showDiagram.set(false);
    }

    onSelect({ selection, trigger }: DiagramSelectEvent) {
        this.selection.set(selection);
        this.selectionTrigger = trigger;
        TelemetryService.sendWithSub(TELEMETRY_CATEGORY, this.paneTelemetrySub(selection), "PaneOpen");
        const content = this.paneContent();
        if (content && this.paneMode() === "region") {
            AriaLive.announce(`Showing details for ${paneHeading(content)}`);
        }
    }

    selectFromPane(selection: DiagramSelection) {
        this.selection.set(selection);
        TelemetryService.sendWithSub(TELEMETRY_CATEGORY, this.paneTelemetrySub(selection), "PaneOpen");
    }

    closePane() {
        this.selection.set(null);
        const trigger = this.selectionTrigger;
        this.selectionTrigger = null;
        if (trigger?.isConnected) trigger.focus();
    }

    onSpawnedMore() {
        TelemetryService.send(TELEMETRY_CATEGORY, "SpawnedMore");
    }

    requestChat(request: DiagramChatRequest) {
        TelemetryService.send(TELEMETRY_CATEGORY, "ChatStart");
        this.chatWith.emit(request);
    }

    /** Chat from the sheet closes it first; when chat closes, focus goes back to whatever opened the sheet. */
    requestChatFromPane(request: DiagramChatRequest) {
        if (this.paneMode() !== "dialog") {
            this.requestChat(request);
            return;
        }
        const sheetTrigger = this.selectionTrigger;
        this.selectionTrigger = null;
        this.selection.set(null);
        this.requestChat({ ...request, returnFocusTo: sheetTrigger });
    }

    private paneTelemetrySub(selection: DiagramSelection): string {
        switch (selection.kind) {
            case "stage":
                return "Stage";
            case "action":
                return "Action";
            case "spawned":
                return "SpawnedForm";
            default:
                return S25Assert.never(selection, "Unknown diagram selection");
        }
    }

    private stageConfig = computed(() => ({
        width: this.showProgress() ? 280 : 200,
        minHeight: 105,
        horizontalGap: 120,
        verticalGap: 60,
        padding: 40,
    }));

    @HostListener("document:keydown", ["$event"])
    handleKeyboardEvent(event: KeyboardEvent) {
        // Check if the workflow diagram viewer is visible/focused
        if (!this.canvasWrapper()?.nativeElement?.offsetParent) return;

        // Ctrl/Cmd + Plus for zoom in
        if ((event.ctrlKey || event.metaKey) && (event.key === "+" || event.key === "=")) {
            event.preventDefault();
            this.zoomIn();
        }
        // Ctrl/Cmd + Minus for zoom out
        else if ((event.ctrlKey || event.metaKey) && event.key === "-") {
            event.preventDefault();
            this.zoomOut();
        }
    }

    diagramStages = computed(() => {
        const process = this.workflowProcess();
        if (!process?.stages || process.stages.length === 0) return [];

        const stages = process.stages;
        const transitions = process.stageTransitions || [];

        stages.forEach((stage, index) => {
            if (!stage.id) {
                stage.id = stage.workflowStageId || -(index + 1);
            }
        });

        const connections = new Map<string, Set<string>>();
        const incomingConnections = new Map<string, Set<string>>();

        transitions.forEach((t) => {
            if (!connections.has(t.fromStageName)) {
                connections.set(t.fromStageName, new Set());
            }
            connections.get(t.fromStageName)!.add(t.toStageName);

            if (!incomingConnections.has(t.toStageName)) {
                incomingConnections.set(t.toStageName, new Set());
            }
            incomingConnections.get(t.toStageName)!.add(t.fromStageName);
        });

        const layoutStages: DiagramStage[] = [];
        const stageLevels = new Map<string, number>();

        if (transitions.length === 0) {
            const sorted = [...stages].sort((a, b) => {
                if (a.isStartStage !== b.isStartStage) return a.isStartStage ? -1 : 1;
                if ((a.sortOrder ?? 0) !== (b.sortOrder ?? 0)) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
                return a.stageName.localeCompare(b.stageName);
            });
            sorted.forEach((stage, i) => stageLevels.set(stage.stageName, i));
        } else {
            const startStages = stages.filter((s) => s.isStartStage || !incomingConnections.has(s.stageName));
            const initialStages = startStages.length > 0 ? startStages : [stages[0]];
            const queue: { stage: (typeof stages)[0]; level: number }[] = initialStages.map((s) => ({
                stage: s,
                level: 0,
            }));

            while (queue.length > 0) {
                const { stage, level } = queue.shift()!;

                if (!stageLevels.has(stage.stageName) || stageLevels.get(stage.stageName)! > level) {
                    stageLevels.set(stage.stageName, level);

                    const nextStageNames = connections.get(stage.stageName) || new Set();
                    nextStageNames.forEach((nextName) => {
                        const nextStage = stages.find((s) => s.stageName === nextName);
                        if (nextStage) {
                            queue.push({ stage: nextStage, level: level + 1 });
                        }
                    });
                }
            }
        }

        const levelGroups = new Map<number, typeof stages>();
        stages.forEach((stage) => {
            const level = stageLevels.get(stage.stageName) ?? 0;
            if (!levelGroups.has(level)) {
                levelGroups.set(level, []);
            }
            levelGroups.get(level)!.push(stage);
        });

        const sortedLevels = Array.from(levelGroups.keys()).sort((a, b) => a - b);

        const live = this.liveDiagram();
        const groupIdToName = this.apGroupStore.groupNames();
        const contIdToName = this.approvalContactNames.value();

        sortedLevels.forEach((level) => {
            const stagesAtLevel = levelGroups.get(level)!;

            stagesAtLevel.sort((a, b) => {
                if (a.isStartStage !== b.isStartStage) {
                    return a.isStartStage ? -1 : 1;
                }
                return a.stageName.localeCompare(b.stageName);
            });

            const x =
                this.stageConfig().padding + level * (this.stageConfig().width + this.stageConfig().horizontalGap);
            const hasProgress = !!(this.stageStatuses()?.size || this.currentStageId() != null);
            let y = this.stageConfig().padding + (hasProgress ? 20 : 0);

            stagesAtLevel.forEach((stage) => {
                const detail =
                    stage.workflowStageId != null ? this.stageIdToDetail().get(stage.workflowStageId) : undefined;
                const flagLabels = stageFlagLabels(stage);
                const headerHeight =
                    this.calculateHeaderHeight(stage.stageName) + (flagLabels.length ? DiagramRowHeights.flags : 0);
                const states = stage.states ?? [];
                const actions = this.actionRows(stage, detail);
                const accumulatorSpawned =
                    live && stage.isAccumulator && detail
                        ? spawnedGroup(`accumulated-${stage.id}`, this.uniqueSpawnedForms(detail.spawnedForms))
                        : undefined;
                const approvalRows = live
                    ? liveApprovalRows(detail, states.length)
                    : configuredApprovalRows(states, contIdToName, groupIdToName);
                const submitter =
                    live?.submitter && stage.isStartStage
                        ? toPersonRow(live.submitter, "submitter", "Submitter")
                        : undefined;

                const bodyHeight = stageBodyHeight({
                    actions,
                    accumulatorSpawned,
                    approvalRows,
                    submitter,
                    live: !!live,
                });
                const height = this.compactMode()
                    ? this.stageConfig().minHeight
                    : Math.max(this.stageConfig().minHeight, headerHeight + bodyHeight);

                layoutStages.push({
                    id: `stage-${stage.id}`,
                    stageId: stage.id,
                    workflowStageId: stage.workflowStageId,
                    name: stage.stageName,
                    x,
                    y,
                    width: this.stageConfig().width,
                    height,
                    headerHeight,
                    type: stageBaseType(stage),
                    colorType: stageColorType(stage),
                    flagLabels,
                    states: states.map((state, index) =>
                        this.diagramState(state, index, detail, contIdToName, groupIdToName),
                    ),
                    actions,
                    accumulatorSpawned,
                    approvalRows,
                    submitter,
                });

                y += height + this.stageConfig().verticalGap;
            });
        });

        const statusMap = this.stageStatuses();
        if (statusMap?.size) {
            this.applyStageStatusMap(layoutStages, stageLevels, statusMap);
        } else {
            const activeStageId = this.currentStageId();
            if (activeStageId != null) {
                this.assignStageStatuses(layoutStages, stageLevels, activeStageId);
            }
        }

        return layoutStages;
    });

    private assignStageStatuses(stages: DiagramStage[], stageLevels: Map<string, number>, activeStageId: number) {
        const currentStage = stages.find((s) => s.stageId === activeStageId);
        if (!currentStage) return;

        const currentLevel = stageLevels.get(currentStage.name);
        if (currentLevel == null) return;

        for (const stage of stages) {
            const level = stageLevels.get(stage.name);
            if (level == null) continue;
            if (stage.stageId === activeStageId) {
                stage.status = "current";
                stage.statusLabel = "In Progress";
            } else if (level < currentLevel) {
                stage.status = "completed";
                stage.statusLabel = "Completed";
            } else {
                stage.status = "pending";
                stage.statusLabel = "Pending";
            }
        }
    }

    private applyStageStatusMap(
        stages: DiagramStage[],
        stageLevels: Map<string, number>,
        statusMap: Map<number, StageStatusInfo>,
    ) {
        let maxKnownLevel = -1;
        for (const stage of stages) {
            const info =
                (stage.workflowStageId != null ? statusMap.get(stage.workflowStageId) : undefined) ??
                (stage.stageId != null ? statusMap.get(stage.stageId) : undefined);
            if (!info) continue;
            stage.status = info.status;
            stage.statusLabel = info.label;
            const level = stageLevels.get(stage.name) ?? -1;
            if (info.status !== "pending") maxKnownLevel = Math.max(maxKnownLevel, level);
        }

        for (const stage of stages) {
            if (stage.status) continue;
            const level = stageLevels.get(stage.name);
            if (level == null) continue;
            const inferred = level <= maxKnownLevel ? "completed" : "pending";
            stage.status = inferred;
            stage.statusLabel = inferred === "completed" ? "Completed" : "Pending";
        }
    }

    private actionRows(
        stage: Workflow.WorkflowStage,
        detail: Workflow.DiagramStageDetail | undefined,
    ): DiagramActionRow[] {
        const live = this.isLive();
        const formIdToName = this.spawnFormNames.value();
        const actions =
            live && detail ? filterUsedActions(stage.actions ?? [], detail.usedActionIds) : (stage.actions ?? []);
        return actions.map((action, index) => {
            const id = `action-${action.workflowActionId ?? action.id ?? index}`;
            const isSpawn = action.actionCode === "SPAWN_WORKFLOW";
            const spawnedForms = (detail?.spawnedForms ?? []).filter(
                (form) => form.formId === action.spawnChildParentItemId,
            );
            return {
                id,
                actionId: action.workflowActionId,
                type: action.actionType,
                code: action.actionCode,
                label: actionLabel(action.actionType, action.actionCode),
                configured: action,
                detail: detail?.actions.find(
                    (candidate) => candidate.originalWorkflowActionId === action.workflowActionId,
                ),
                isSpawn,
                spawnFormNames: isSpawn
                    ? spawnTargetFormIds(action).map((formId) => spawnFormLabel(formId, formIdToName))
                    : undefined,
                spawned: live && isSpawn ? spawnedGroup(`spawned-${id}`, spawnedForms) : undefined,
            };
        });
    }

    /** An accumulator that also has a Spawn action gets the same child from both paths. */
    private uniqueSpawnedForms(forms: Workflow.DiagramSpawnedForm[]): Workflow.DiagramSpawnedForm[] {
        const seenKeys = new Set<string>();
        return forms.filter((form) => {
            const key = `${form.itemTypeId}-${form.itemId}`;
            if (seenKeys.has(key)) return false;
            seenKeys.add(key);
            return true;
        });
    }

    private diagramState(
        state: Workflow.WorkflowState,
        index: number,
        detail: Workflow.DiagramStageDetail | undefined,
        contIdToName: Record<number, string> | undefined,
        groupIdToName: Map<number, string>,
    ): DiagramState {
        const stateId = state.workflowStateId ?? state.id;
        return {
            id: `state-${stateId ?? index}`,
            name: state.stateName,
            approvalGroupName:
                state.approvalGroupId != null
                    ? (groupIdToName.get(state.approvalGroupId) ?? "Approval group")
                    : undefined,
            contacts: (state.contacts ?? []).map((contact) => ({
                contId: contact.contId,
                name: contIdToName?.[contact.contId] ?? contact.contactName ?? RESTRICTED_CONTACT_LABEL,
            })),
            orgRoleNames: (state.orgRoles ?? []).map((orgRole) => orgRole.contRole?.roleName ?? RESTRICTED_VALUE_LABEL),
            hasAiApprover: !!state.hasAiApprover,
            aiOutcome: detail?.aiOutcomes.find((aiOutcome) => aiOutcome.originalWorkflowStateId === stateId)?.outcome,
        };
    }

    diagramTransitions = computed(() => {
        const process = this.workflowProcess();
        const stages = this.diagramStages();

        // Access properties to ensure reactivity
        const transitionData = process?.stageTransitions || [];
        const stageCount = stages.length;

        if (transitionData.length === 0 || stageCount === 0) {
            return [];
        }

        const transitions: DiagramTransition[] = [];
        const processedPairs = new Set<string>();

        // Process each transition
        transitionData.forEach((t, i) => {
            // Try to find stages by name first, then by ID
            let fromStage: DiagramStage | undefined;
            let toStage: DiagramStage | undefined;

            if (t.fromStageName && t.toStageName) {
                fromStage = stages.find((s) => s.name === t.fromStageName);
                toStage = stages.find((s) => s.name === t.toStageName);
            } else if (t.fromStageId && t.toStageId) {
                // Fall back to matching by stage ID
                fromStage = stages.find((s) => s.stageId === t.fromStageId);
                toStage = stages.find((s) => s.stageId === t.toStageId);
            } else {
                return;
            }

            if (fromStage && toStage) {
                // Create a unique key for this stage pair (order-independent)
                const pairKey = [fromStage.id, toStage.id].sort().join("-");
                const isFirstInPair = !processedPairs.has(pairKey);
                processedPairs.add(pairKey);

                // Check if there's a reverse transition for bidirectional handling
                const reverseTransition = transitionData.find(
                    (other) =>
                        other !== t &&
                        ((other.fromStageName === t.toStageName && other.toStageName === t.fromStageName) ||
                            (other.fromStageId === t.toStageId && other.toStageId === t.fromStageId)),
                );

                const hasBidirectional = reverseTransition !== undefined;

                // For bidirectional transitions, curve direction depends on whether this is the first or second
                const curveDirection = hasBidirectional && !isFirstInPair;

                const path = this.calculateTransitionPath(fromStage, toStage, hasBidirectional, curveDirection ? 1 : 0);
                transitions.push({
                    id: `transition-${t.id ?? i}`,
                    from: fromStage.id,
                    to: toStage.id,
                    label: t.transitionLabel || "",
                    type: t.transitionType || "APPROVE",
                    path: path || `M 0 0 L 100 100`,
                });
            }
        });

        return transitions;
    });

    readonly labeledTransitions = computed(() => this.diagramTransitions().filter((transition) => transition.label));

    protected readonly PROGRESS_Y = 12;
    protected readonly PROGRESS_R = 11;
    protected readonly PILL_CENTER_Y = 34;

    readonly progressNodes = computed(() => {
        const stages = this.diagramStages();
        if (this.currentStageId() == null && !this.stageStatuses()?.size) return [];
        if (stages.length === 0) return [];

        const byX = new Map<number, DiagramStage[]>();
        for (const stage of stages) {
            if (!byX.has(stage.x)) byX.set(stage.x, []);
            byX.get(stage.x)!.push(stage);
        }

        return [...byX.entries()]
            .sort(([a], [b]) => a - b)
            .map(([x, stagesAtX]) => {
                const centerX = x + this.stageConfig().width / 2;
                let status: StageStatus;
                if (stagesAtX.some((s) => s.status === "current")) status = "current";
                else if (stagesAtX.some((s) => s.status === "denied")) status = "denied";
                else if (stagesAtX.some((s) => s.status === "cancelled")) status = "cancelled";
                else if (stagesAtX.some((s) => s.status === "changes_requested")) status = "changes_requested";
                else if (stagesAtX.every((s) => s.status === "completed")) status = "completed";
                else status = "pending";
                const label = stagesAtX.find((s) => s.status === status)?.statusLabel ?? "";
                const labelWidth = label.length * 6.5 + 16;
                return { x: centerX, status, label, labelWidth };
            });
    });

    readonly progressSegments = computed(() => {
        const nodes = this.progressNodes();
        const segments: { x1: number; x2: number; filled: boolean; status: StageStatus }[] = [];
        for (let i = 1; i < nodes.length; i++) {
            segments.push({
                x1: nodes[i - 1].x,
                x2: nodes[i].x,
                filled: nodes[i].status !== "pending",
                status: nodes[i].status,
            });
        }
        return segments;
    });

    progressNodeColor(status: StageStatus): string {
        switch (status) {
            case "completed":
                return "#4ade80";
            case "current":
                return "#3b82f6";
            case "denied":
                return "#f87171";
            case "cancelled":
                return "#d1d5db";
            case "changes_requested":
                return "#fbbf24";
            case "pending":
                return "#e2e8f0";
        }
    }

    progressPillColor(status: StageStatus): string {
        switch (status) {
            case "completed":
                return "#dcfce7";
            case "changes_requested":
                return "#fef3c7";
            default:
                return this.progressNodeColor(status);
        }
    }

    progressPillTextColor(status: StageStatus): string {
        switch (status) {
            case "completed":
                return "#166534";
            case "changes_requested":
                return "#78350f";
            case "pending":
            case "cancelled":
                return "#475569";
            default:
                return "white";
        }
    }

    progressNodeIcon(status: StageStatus): string {
        switch (status) {
            case "completed":
                return "✓";
            case "denied":
                return "✗";
            case "cancelled":
                return "—";
            case "changes_requested":
                return "↩";
            default:
                return "";
        }
    }

    svgWidth = computed(() => {
        const stages = this.diagramStages();
        if (stages.length === 0) return 800;

        let maxX = 800;
        stages.forEach((stage) => {
            maxX = Math.max(maxX, stage.x + stage.width);
        });

        return maxX + this.stageConfig().padding * 2;
    });

    svgHeight = computed(() => {
        const stages = this.diagramStages();
        if (stages.length === 0) return 600;

        let maxY = 600;
        stages.forEach((stage) => {
            maxY = Math.max(maxY, stage.y + stage.height);
        });

        return maxY + this.stageConfig().padding * 2;
    });

    viewBox = computed(() => {
        return `0 0 ${this.svgWidth()} ${this.svgHeight()}`;
    });

    private calculateHeaderHeight(stageName: string): number {
        // Available width is about 150px (200px stage - 50px for icon/padding)
        // With 16px font and font-weight: 600, approximately 20 characters fit on one line
        const trimmedName = stageName.trim();
        const textLength = trimmedName.length;

        // Quick check for obviously single-line text
        if (textLength <= 20) {
            return 45; // Single line header
        }

        // For longer text, check word wrapping
        const words = trimmedName.split(/\s+/);
        let lines = 1;
        let currentLineLength = 0;
        const maxCharsPerLine = 20; // Actual capacity with 150px width

        for (const word of words) {
            // Check if word alone is too long for a line
            if (word.length > maxCharsPerLine) {
                // Long word will be broken
                const wordLines = Math.ceil(word.length / maxCharsPerLine);
                lines += wordLines - 1;
                currentLineLength = word.length % maxCharsPerLine;
            } else {
                const wordLengthWithSpace = currentLineLength > 0 ? word.length + 1 : word.length;

                if (currentLineLength + wordLengthWithSpace > maxCharsPerLine) {
                    // Word would overflow, start new line
                    lines++;
                    currentLineLength = word.length;
                } else {
                    currentLineLength += wordLengthWithSpace;
                }
            }
        }

        // Cap at 3 lines maximum
        lines = Math.min(lines, 3);

        // Consistent height per line count
        if (lines === 1) {
            return 45; // Single line header
        } else if (lines === 2) {
            return 65; // Two line header
        } else {
            return 80; // Three line header
        }
    }

    zoomIn(event?: MouseEvent) {
        event?.stopPropagation();
        const currentZoom = this.zoom();
        const newZoom = Math.min(currentZoom + this.ZOOM_STEP, this.MAX_ZOOM);
        this.zoom.set(newZoom);
        this.showZoomIndicatorBriefly();
    }

    zoomOut(event?: MouseEvent) {
        event?.stopPropagation();
        const currentZoom = this.zoom();
        const newZoom = Math.max(currentZoom - this.ZOOM_STEP, this.MIN_ZOOM);
        this.zoom.set(newZoom);
        this.showZoomIndicatorBriefly();
    }

    fitToView(event?: MouseEvent) {
        event?.stopPropagation();
        const wrapper = this.canvasWrapper()?.nativeElement;
        if (!wrapper) return;

        const stages = this.diagramStages();
        if (stages.length === 0) return;

        // Calculate the actual bounds of the diagram
        let maxX = 0;
        let maxY = 0;
        stages.forEach((stage) => {
            maxX = Math.max(maxX, stage.x + stage.width);
            maxY = Math.max(maxY, stage.y + stage.height);
        });

        // Add padding
        const padding = this.stageConfig().padding * 2;
        const diagramWidth = maxX + padding;
        const diagramHeight = maxY + padding;

        // Get the viewport dimensions
        const viewportWidth = wrapper.clientWidth - 80; // Account for scrollbar and padding
        const viewportHeight = wrapper.clientHeight - 80;

        // Calculate zoom to fit both width and height
        const zoomX = viewportWidth / diagramWidth;
        const zoomY = viewportHeight / diagramHeight;

        // Use the smaller zoom to ensure entire diagram fits
        let targetZoom = Math.min(zoomX, zoomY);

        // Constrain to min/max zoom bounds
        targetZoom = Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, targetZoom));

        // Round to nearest ZOOM_STEP for cleaner values
        targetZoom = Math.round(targetZoom / this.ZOOM_STEP) * this.ZOOM_STEP;

        this.zoom.set(targetZoom);
        this.showZoomIndicatorBriefly();

        // Reset scroll to top-left
        setTimeout(() => {
            wrapper.scrollLeft = 0;
            wrapper.scrollTop = 0;
        }, 0);
    }

    resetZoom(event?: MouseEvent) {
        event?.stopPropagation();
        this.zoom.set(this.DEFAULT_ZOOM);
        this.showZoomIndicatorBriefly();

        // Optionally center the view on the first stage
        const wrapper = this.canvasWrapper()?.nativeElement;
        if (wrapper) {
            setTimeout(() => {
                wrapper.scrollLeft = 0;
                wrapper.scrollTop = 0;
            }, 0);
        }
    }

    /** Stale content stays mounted while reloading; the Refresh button is never disabled. */
    async refreshStatus() {
        if (!this.processInstanceId()) return;
        const pendingAnnouncement = setTimeout(
            () => AriaLive.announce("Refreshing workflow status"),
            REFRESH_PENDING_ANNOUNCE_DELAY_MS,
        );
        await this.diagram.reloadAndAwait();
        clearTimeout(pendingAnnouncement);
        if (this.destroyRef.destroyed) return;
        if (this.diagram.error()) {
            AriaLive.announce("Couldn't refresh workflow status");
            return;
        }
        AriaLive.announce(this.statusUpdateMessage());
    }

    retryDiagram() {
        void this.refreshStatus();
    }

    private statusUpdateMessage(): string {
        const stages = this.diagramStages();
        const current =
            stages.find((stage) => stage.status === "current") ??
            stages.filter((stage) => stage.status && stage.status !== "pending").at(-1);
        if (!current?.statusLabel) return "Workflow status updated";
        return `Workflow status updated: ${current.name}, ${current.statusLabel}`;
    }

    private showZoomIndicatorBriefly() {
        this.showZoomIndicator.set(true);
        if (this.zoomIndicatorTimeout) {
            clearTimeout(this.zoomIndicatorTimeout);
        }
        this.zoomIndicatorTimeout = window.setTimeout(() => {
            this.showZoomIndicator.set(false);
        }, 1500);
    }

    onWheel(event: WheelEvent) {
        // Only zoom when Ctrl/Cmd is pressed
        if (event.ctrlKey || event.metaKey) {
            event.preventDefault();

            const delta = event.deltaY > 0 ? -this.ZOOM_STEP / 2 : this.ZOOM_STEP / 2;
            const currentZoom = this.zoom();
            const newZoom = Math.max(this.MIN_ZOOM, Math.min(this.MAX_ZOOM, currentZoom + delta));

            if (newZoom !== currentZoom) {
                // Calculate mouse position relative to the canvas wrapper
                const wrapper = this.canvasWrapper()?.nativeElement;
                if (wrapper) {
                    const rect = wrapper.getBoundingClientRect();
                    const mouseX = event.clientX - rect.left;
                    const mouseY = event.clientY - rect.top;

                    // Calculate scroll position to keep the mouse point stationary
                    const scrollX = wrapper.scrollLeft;
                    const scrollY = wrapper.scrollTop;

                    const contentX = scrollX + mouseX;
                    const contentY = scrollY + mouseY;

                    const scale = newZoom / currentZoom;

                    this.zoom.set(newZoom);
                    this.showZoomIndicatorBriefly();

                    // Adjust scroll position to keep content under mouse stationary
                    setTimeout(() => {
                        wrapper.scrollLeft = contentX * scale - mouseX;
                        wrapper.scrollTop = contentY * scale - mouseY;
                    }, 0);
                }
            }
        }
    }

    getArrowMarkerId(type: string): string {
        const markerMap: Record<string, string> = {
            APPROVE: "approve",
            DENY: "deny",
            REQUEST_CHANGES: "changes",
            CANCEL: "cancel",
        };
        return markerMap[type] || "default";
    }

    private calculateTransitionPath(
        from: DiagramStage,
        to: DiagramStage,
        isBidirectional: boolean,
        index: number,
    ): string {
        // For horizontal alignment, use a consistent position across headers
        // Use a fixed offset from top for cleaner horizontal arrows
        const standardOffset = 30; // Standard offset from top of stage for arrows
        const fromAlignedY = Math.min(standardOffset, from.headerHeight / 2);
        const toAlignedY = Math.min(standardOffset, to.headerHeight / 2);

        const fromCenter = {
            x: from.x + from.width / 2,
            y: from.y + fromAlignedY, // Use aligned Y based on header height
        };
        const toCenter = {
            x: to.x + to.width / 2,
            y: to.y + toAlignedY, // Use aligned Y based on header height
        };

        const dx = toCenter.x - fromCenter.x;
        const dy = toCenter.y - fromCenter.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance === 0) return ""; // Same stage

        // For primarily horizontal transitions, connect at matching heights
        const isHorizontal = Math.abs(dx) > Math.abs(dy) * 2; // Consider horizontal if x-distance is more than 2x y-distance

        if (isHorizontal && !isBidirectional) {
            // For horizontal arrows, use the standard offset for both stages
            const standardOffset = 30;
            const startX = from.x + from.width;
            const startY = from.y + standardOffset;
            const endX = to.x;
            const endY = to.y + standardOffset;

            return `M ${startX} ${startY} L ${endX} ${endY}`;
        }

        // Unit vector from from to to
        const unitX = dx / distance;
        const unitY = dy / distance;

        // Calculate proper edge intersection points
        const [fromOffsetX, fromOffsetY] = this.getFromOffset(
            unitX,
            unitY,
            from.width,
            from.height,
            fromAlignedY - from.height / 2,
        );
        const [toOffsetX, toOffsetY] = this.getToOffset(unitX, unitY, to.width, to.height, toAlignedY - to.height / 2);

        const startX = fromCenter.x + fromOffsetX;
        const startY = fromCenter.y + fromOffsetY;
        const endX = toCenter.x + toOffsetX;
        const endY = toCenter.y + toOffsetY;

        if (!isBidirectional) {
            // Straight line for unidirectional transitions
            return `M ${startX} ${startY} L ${endX} ${endY}`;
        }

        // For bidirectional transitions, create curved paths that go in opposite directions
        // Use the index to determine curve direction for consistent separation
        const curveUp = index % 2 === 0;
        const curveOffset = 60; // Amount of curve offset
        const [controlX, controlY] = this.getControlPoints(startX, startY, endX, endY, curveUp, curveOffset);

        return `M ${startX} ${startY} Q ${controlX} ${controlY} ${endX} ${endY}`;
    }

    private getFromOffset(
        unitX: number,
        unitY: number,
        width: number,
        height: number,
        yAdjust: number = 0,
    ): [number, number] {
        let offsetX: number, offsetY: number;
        if (Math.abs(unitX) > Math.abs(unitY)) {
            // More horizontal - connect to left/right edge at aligned position
            offsetX = (Math.sign(unitX) * width) / 2;
            // Clamp the Y offset to keep arrow at reasonable position on the stage
            const maxYOffset = height / 2 - Math.abs(yAdjust);
            offsetY = Math.max(-maxYOffset, Math.min(maxYOffset, (unitY * width) / 2 / Math.abs(unitX) + yAdjust));
        } else {
            // More vertical - connect to top/bottom edge
            offsetX = (unitX * height) / 2 / Math.abs(unitY);
            offsetY = (Math.sign(unitY) * height) / 2;
        }
        return [offsetX, offsetY];
    }

    private getToOffset(
        unitX: number,
        unitY: number,
        width: number,
        height: number,
        yAdjust: number = 0,
    ): [number, number] {
        let offsetX: number, offsetY: number;
        if (Math.abs(unitX) > Math.abs(unitY)) {
            // More horizontal - connect to left/right edge at aligned position
            offsetX = (-Math.sign(unitX) * width) / 2;
            // Clamp the Y offset to keep arrow at reasonable position on the stage
            const maxYOffset = height / 2 - Math.abs(yAdjust);
            offsetY = Math.max(-maxYOffset, Math.min(maxYOffset, (-unitY * width) / 2 / Math.abs(unitX) + yAdjust));
        } else {
            // More vertical - connect to top/bottom edge
            offsetX = (-unitX * height) / 2 / Math.abs(unitY);
            offsetY = (-Math.sign(unitY) * height) / 2;
        }
        return [offsetX, offsetY];
    }

    private getControlPoints(
        startX: number,
        startY: number,
        endX: number,
        endY: number,
        curveUp: boolean,
        curveOffset: number,
    ): [number, number] {
        const midX = (startX + endX) / 2;
        const midY = (startY + endY) / 2;

        let controlX: number, controlY: number;
        if (Math.abs(endX - startX) > Math.abs(endY - startY)) {
            // More horizontal than vertical - offset vertically
            controlX = midX;
            controlY = midY + (curveUp ? -curveOffset : curveOffset);
        } else {
            // More vertical than horizontal - offset horizontally
            controlX = midX + (curveUp ? curveOffset : -curveOffset);
            controlY = midY;
        }
        return [controlX, controlY];
    }

    getTransitionLabelPosition(transition: DiagramTransition) {
        if (!transition.path) return { x: 100, y: 100 };

        try {
            // Check if it's a straight line (M x y L x y) or curved (M x y Q x y x y)
            const straightMatch = transition.path.match(/M\s*([\d.-]+)\s+([\d.-]+)\s+L\s*([\d.-]+)\s+([\d.-]+)/);

            if (straightMatch) {
                const x1 = parseFloat(straightMatch[1]);
                const y1 = parseFloat(straightMatch[2]);
                const x2 = parseFloat(straightMatch[3]);
                const y2 = parseFloat(straightMatch[4]);

                // For straight lines, position at midpoint with perpendicular offset
                const midX = (x1 + x2) / 2;
                const midY = (y1 + y2) / 2;

                // Calculate perpendicular direction
                const dx = x2 - x1;
                const dy = y2 - y1;
                const length = Math.sqrt(dx * dx + dy * dy);

                if (length === 0) return { x: midX, y: midY - 20 };

                // Perpendicular vector (rotate 90 degrees counter-clockwise)
                const perpX = -dy / length;
                const perpY = dx / length;

                // Offset label perpendicular to the line (above it)
                const offset = 15; // Distance from line

                return {
                    x: midX + perpX * offset,
                    y: midY + perpY * offset,
                };
            }

            // Check for quadratic curve
            const curveMatch = transition.path.match(
                /M\s*([\d.-]+)\s+([\d.-]+)\s+Q\s*([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)\s+([\d.-]+)/,
            );

            if (curveMatch) {
                const cx = parseFloat(curveMatch[3]);
                const cy = parseFloat(curveMatch[4]);

                // Use the control point to position the label
                // This ensures the label is on the same side as the curve
                return {
                    x: cx,
                    y: cy,
                };
            }
        } catch (e) {
            console.error("Error calculating label position:", e);
        }

        return { x: 200, y: 200 };
    }
}
